import { z } from "zod";
import { IsoDateTimeSchema, StableIdSchema } from "./common.js";
import { ConnectionStateSchema } from "./connection.js";

export const DiagnosticComponentSchema = z.enum([
  "herskin",
  "hermes_serve",
  "hermes_gateway",
  "controller",
]);

export const DiagnosticLogLevelSchema = z.enum([
  "debug",
  "info",
  "notice",
  "warning",
  "error",
  "critical",
]);

export const DiagnosticLogEntrySchema = z
  .object({
    id: StableIdSchema,
    timestamp: IsoDateTimeSchema,
    level: DiagnosticLogLevelSchema,
    component: DiagnosticComponentSchema,
    event: z.string().min(1).max(80),
    message: z.string().min(1).max(1_200),
  })
  .strict();

export const DiagnosticLogQuerySchema = z
  .object({
    // Fastify 对单个重复查询参数返回 string，对多个返回 string[]；统一归一化，
    // 避免选择单个 Gateway 时被误报“expected array”。
    components: z.preprocess(
      (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
      z.array(DiagnosticComponentSchema).min(1).max(4).optional(),
    ),
    levels: z.preprocess(
      (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
      z.array(DiagnosticLogLevelSchema).min(1).max(6).optional(),
    ),
    cursor: z.string().min(1).max(512).nullable().optional(),
    since: IsoDateTimeSchema.optional(),
    until: IsoDateTimeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .refine(
    (value) => !value.since || !value.until || Date.parse(value.since) <= Date.parse(value.until),
    { message: "日志开始时间不能晚于结束时间", path: ["until"] },
  )
  .strict();

export const DiagnosticLogPageSchema = z
  .object({
    entries: z.array(DiagnosticLogEntrySchema).max(200),
    nextCursor: z.string().min(1).max(512).nullable(),
    truncated: z.boolean(),
    updatedAt: IsoDateTimeSchema,
    availableFrom: IsoDateTimeSchema.nullable(),
    availableTo: IsoDateTimeSchema.nullable(),
    retention: z
      .object({
        managedBy: z.literal("systemd-journald"),
        maxRetentionSeconds: z.number().int().positive().nullable(),
        policy: z.enum(["time_and_size", "size_only"]),
      })
      .strict(),
  })
  .strict();

export const ManagedServiceNameSchema = z.enum([
  "herskin",
  "controller",
  "hermes_serve",
  "hermes_gateway",
]);

export const ManagedServiceStatusSchema = z
  .object({
    service: ManagedServiceNameSchema,
    status: z.enum(["online", "degraded", "offline", "unknown"]),
    label: z.string().min(1).max(80),
    detail: z.string().min(1).max(240),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

const ByteCountSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);

/**
 * 宿主机只读资源快照。Hermes 数据目录仅暴露汇总值，绝不把服务器绝对路径送入浏览器。
 */
export const ServerMetricsSchema = z
  .object({
    cpu: z
      .object({
        utilizationPercent: z.number().min(0).max(100),
        load1: z.number().nonnegative(),
        load5: z.number().nonnegative(),
        logicalCores: z.number().int().positive().max(4_096),
      })
      .strict(),
    memory: z
      .object({
        totalBytes: ByteCountSchema,
        usedBytes: ByteCountSchema,
        availableBytes: ByteCountSchema,
        usedPercent: z.number().min(0).max(100),
      })
      .strict(),
    disk: z
      .object({
        totalBytes: ByteCountSchema,
        usedBytes: ByteCountSchema,
        availableBytes: ByteCountSchema,
        usedPercent: z.number().min(0).max(100),
      })
      .strict(),
    uptimeSeconds: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    hermesData: z
      .object({
        usedBytes: ByteCountSchema,
        fileCount: ByteCountSchema,
        truncated: z.boolean(),
      })
      .strict()
      .nullable(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

const UsageCountSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const UsageDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "用量日期必须是 YYYY-MM-DD");

export const UsageDailyPointSchema = z
  .object({
    date: UsageDaySchema,
    inputTokens: UsageCountSchema,
    outputTokens: UsageCountSchema,
    cacheReadTokens: UsageCountSchema,
    reasoningTokens: UsageCountSchema,
    totalTokens: UsageCountSchema,
    sessions: UsageCountSchema,
    apiCalls: UsageCountSchema,
  })
  .strict();

/** Hermes 官方 analytics 统计。日分桶由 Hermes 固定为 UTC，会话数量按启动日归属。 */
export const UsageOverviewSchema = z
  .object({
    available: z.boolean(),
    source: z.literal("hermes_analytics"),
    dayTimezone: z.literal("UTC"),
    today: UsageDailyPointSchema,
    todayUserMessages: UsageCountSchema,
    todayUserMessagesScope: z.literal("herskin_exact_time"),
    sevenDayTokens: UsageCountSchema,
    thirtyDayTokens: UsageCountSchema,
    peakDay: UsageDailyPointSchema.nullable(),
    /** 峰值由 HerSkin 持久保留，不受官方 365 天查询窗口滚动影响。 */
    peakScope: z.literal("recorded_history"),
    recordedHistoryStartDate: UsageDaySchema.nullable(),
    /** 趋势与热力图仍使用 Hermes 官方允许的最大窗口。 */
    dailyWindowDays: z.literal(365),
    daily: z.array(UsageDailyPointSchema).max(365),
    auxiliaryUsageIncluded: z.literal(false),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const ManagementOverviewSchema = z
  .object({
    connection: ConnectionStateSchema,
    services: z.array(ManagedServiceStatusSchema).min(3).max(4),
    controllerAvailable: z.boolean(),
    serverMetrics: ServerMetricsSchema.nullable(),
    usage: UsageOverviewSchema,
    currentModel: z.string().min(1).max(240).nullable(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export type DiagnosticComponent = z.infer<typeof DiagnosticComponentSchema>;
export type DiagnosticLogLevel = z.infer<typeof DiagnosticLogLevelSchema>;
export type DiagnosticLogEntry = z.infer<typeof DiagnosticLogEntrySchema>;
export type DiagnosticLogQuery = z.infer<typeof DiagnosticLogQuerySchema>;
export type DiagnosticLogPage = z.infer<typeof DiagnosticLogPageSchema>;
export type ManagedServiceStatus = z.infer<typeof ManagedServiceStatusSchema>;
export type ServerMetrics = z.infer<typeof ServerMetricsSchema>;
export type UsageDailyPoint = z.infer<typeof UsageDailyPointSchema>;
export type UsageOverview = z.infer<typeof UsageOverviewSchema>;
export type ManagementOverview = z.infer<typeof ManagementOverviewSchema>;
